import useApiRequest from "@/lib/hooks/use-request";
import { CreateInvoiceRequestDto, InvoiceListResponseDto } from "@/lib/interfaces/invoices";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

const InvoicesEditor = ({ item, type }: { item?: InvoiceListResponseDto; type: "create" | "delete" }) => {
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();

  const invoiceSchema = z.object({
    month: z
      .number()
      .min(1, { message: "Monat muss zwischen 1 und 12 liegen" })
      .max(12, { message: "Monat muss zwischen 1 und 12 liegen" }),
    year: z
      .number()
      .min(2000, { message: "Jahr muss nach 2000 liegen" })
      .max(new Date().getFullYear() + 1, { message: "Jahr kann nicht in der Zukunft liegen" }),
  });

  type InvoiceFormSchema = z.infer<typeof invoiceSchema>;

  const form = useForm<InvoiceFormSchema>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      month: item?.month || new Date().getMonth() + 1,
      year: item?.year || new Date().getFullYear(),
    },
  });

  const onSubmit = async (data: InvoiceFormSchema) => {
    if (type === "delete") {
      await apiRequest(`invoices?year=${data.year}&month=${data.month}`, "DELETE", {
        toast: { toastText: "Rechnung erfolgreich gelöscht" },
      });
      navigate(`/invoices`);
    } else {
      const req: CreateInvoiceRequestDto = {
        month: data.month,
        year: data.year,
      };

      await apiRequest<InvoiceListResponseDto>("invoices", "POST", {
        body: req,
        toast: { toastText: "Rechnung erfolgreich erstellt" },
      });
      navigate(`/invoices`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Monat:*</FormLabel>
              <FormField
                control={form.control}
                name="month"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={1}
                        max={12}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        placeholder="Monat (1-12)"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Jahr:*</FormLabel>
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={2000}
                        max={new Date().getFullYear() + 1}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                        placeholder="Jahr"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <Button
          type="submit"
          variant={type === "create" ? "default" : "destructive"}
          className="text-white font-bold flex items-center gap-4 w-fit self-end"
        >
          <FileText className="h-4 w-4" />
          <span>{type === "create" ? "Rechnung erstellen" : "Rechnung löschen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default InvoicesEditor;
